/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2021, Zumper
 */

import type { Property } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { getOrCreateStorage } from './getOrCreateStorage'

const VIEW_COUNT_KEY = 'detail-view-count'

interface ViewCountRow {
  viewNumber: number
  viewTimes: number[]
}
export interface ViewCount {
  [id: `${'b' | 'l' | 'pb' | 'pl'}-${number}`]: ViewCountRow
}

export const getViewCountObject = () => {
  const storage = getOrCreateStorage()
  let viewCount: ViewCount | null = storage.getItem(VIEW_COUNT_KEY)
  if (!viewCount) {
    viewCount = {}
    updateViewCountObject(viewCount)
  }
  const viewEnd = new Date().valueOf()

  const finalizeRow = (row: ViewCountRow) => {
    if (Array.isArray(row.viewTimes)) {
      row.viewTimes = row.viewTimes.map((viewTime) =>
        viewTime < 0 ? (viewEnd + viewTime) / 1000 : viewTime
      )
    } else {
      row.viewTimes = []
    }
  }

  for (const entity in viewCount) {
    if (
      typeof viewCount[entity as keyof typeof viewCount]?.viewNumber === 'undefined' ||
      typeof viewCount[entity as keyof typeof viewCount]?.viewTimes === 'undefined'
    ) {
      viewCount[entity as keyof typeof viewCount] = { viewNumber: 0, viewTimes: [] }
    } else {
      finalizeRow(viewCount[entity as keyof typeof viewCount])
    }
  }
  return viewCount
}

export const updateViewCountObject = (viewCount: ViewCount) => {
  const storage = getOrCreateStorage()
  storage.setItem(VIEW_COUNT_KEY, viewCount)
}

export const getEntityViewCount = (entity: MaybeEmpty<Property>) => {
  const { buildingId, listingId, pbId, plId } = entity ?? {}
  const viewObject = getViewCountObject()
  const counts: { [key: string]: ViewCountRow } = {}

  if (buildingId) {
    counts[`b-${buildingId}`] = viewObject[`b-${buildingId}`] || { viewNumber: 0, viewTimes: [] }
    if (pbId) {
      counts[`pb-${pbId}`] = viewObject[`pb-${pbId}`] || { viewNumber: 0, viewTimes: [] }
    }
  } else if (listingId) {
    counts[`l-${listingId}`] = viewObject[`l-${listingId}`] || { viewNumber: 0, viewTimes: [] }
    if (plId) {
      counts[`pl-${plId}`] = viewObject[`pl-${plId}`] || { viewNumber: 0, viewTimes: [] }
    }
  }

  return counts
}
